/**
 * Athos request error classification.
 *
 * Split out from the fetcher and the provider because the same predicate decides, in three
 * places, whether a failure is reported or silently dropped. A replaced in-flight request is
 * noise and must stay quiet; anything else is a real failure the caller needs to see. Getting
 * that line wrong is how an API outage came to look like an empty collection.
 *
 * Plain `.mjs` with no imports so `node --test` can cover it without a bundler.
 */

/** Statuses worth one retry: transient server and rate-limit responses, never client errors. */
export const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504])

/**
 * @param {number} status HTTP status from an Athos response.
 * @returns {boolean} Whether a single retry is worthwhile.
 */
export const isRetryableStatus = (status) => RETRYABLE_STATUS.has(status)

/**
 * Whether a thrown error is this request being abandoned rather than failing.
 *
 * Checks the signal as well as the error: aborting a `fetch` mid-flight can surface as the
 * abort reason rather than an `AbortError`, so the signal is the reliable half.
 *
 * @param {unknown} error The thrown value.
 * @param {{aborted?: boolean}} [signal] The signal governing the request, if any.
 * @returns {boolean} True when the failure should be ignored.
 */
export const isAbortError = (error, signal) => Boolean(signal?.aborted) || error?.name === 'AbortError'
